import React, { useState, useEffect } from "react"
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from '@material-ui/core';

import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

import APIS from "../utils/APIS"
import queryString from "query-string"

export const passwordValidation = {
  required: "Veuillez entrer un mot de passe",
  regex: /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ !"#$%&'()*,-.:\;<>?@[\]^_`{|}~])/,
  matches:
    "Doit contenir au moins 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre et un caractère spécial",
  majMinRegex: /(?=.*[a-z])(?=.*[A-Z])/,
  majMinMatches: "Doit contenir au moins 1 majuscule et 1 minuscule",
  digitSpecialCharRegex: /(?=.*\d)(?=.*[ !"#$%&'()*,-.:\;<>?@[\]^_`{|}~])/,
  digitSpecialCharMatches:
    "Doit contenir au moins 1 chiffre et un caractère spécial (les symboles + et = ne sont pas valides)",
  min: "Doit contenir au moins 8 caractères",
}

const ResetPassword = () => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const [isValidRequest, setIsValidRequest] = useState(null)
  const [infos, setInfos] = useState({})
  useEffect(() => {
    const urlParams = queryString.parse(location.search)
    window.history.replaceState(null, null, window.location.pathname)
    if (urlParams.id && urlParams.uuid) {
      APIS.checkResetPsswd(urlParams).then((result) => {
        if (result.message == "ok") {
          setInfos({ id: urlParams.id, uuid: urlParams.uuid })
          setIsValidRequest(true)
        } else {
          setIsValidRequest(false)
        }
      })
    } else {
      setIsValidRequest(false)
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>Mot de passe | TheScan</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          {isValidRequest === "final" &&
            <Box sx={{ mb: 3, textAlign:"center" }}>
              <Typography
                color="textBlack"
                variant="h2"
              >
                Modification mot de passe
              </Typography>
              <Typography
                color="textPrimary"
                gutterBottom
                mt={2}
                variant="body2"
              >
                Informations enregistrées !
              </Typography>
              <Box sx={{ py: 2 }}>
                <RouterLink to="/login">
                  <Button
                    color="primary"
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    endIcon={<ChevronRightIcon/>}
                  >
                    Se connecter
                  </Button>
                </RouterLink>
              </Box>
            </Box>
          }
          {isValidRequest === false &&
            <Box sx={{ mb: 3, textAlign:"center" }}>
              <Typography
                color="textBlack"
                variant="h2"
              >
                Modification mot de passe
              </Typography>
              <Typography
                color="textPrimary"
                gutterBottom
                variant="body2"
              >
                Les informations ne sont pas valides
              </Typography>
            </Box>
          }
          {isValidRequest === true &&
            <Formik
              initialValues={{
                password: '',
                confirm : '',
              }}
              validationSchema={Yup.object().shape({
                password: Yup.string()
                  .min(8, passwordValidation.min)
                  .matches(passwordValidation.regex, passwordValidation.matches)
                  .required(passwordValidation.required),
                confirm: Yup.string()
                  .oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre')
                  .required(passwordValidation.required),
              })}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                APIS.setPsswd({
                  id: infos.id,
                  uuid: infos.uuid,
                  password: values.password,
                  confirm : values.confirm,
                }).then(result => {
                  if (result?.message == "ok") {
                    resetForm({})
                    setIsValidRequest("final")
                  } else {
                    resetForm({})
                    setSubmitting(false);
                    alert("Une erreur est survenue")
                  }
                })
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                setFieldValue,
                handleSubmit,
                isSubmitting,
                touched,
                values
              }) => (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ mb: 3, textAlign:"center" }}>
                    <Typography
                      color="textBlack"
                      variant="h2"
                    >
                      Modification mot de passe
                    </Typography>
                  </Box>
                  <TextField
                    error={Boolean(touched.password && errors.password)}
                    fullWidth
                    helperText={touched.password && errors.password}
                    label="Mot de passe"
                    margin="normal"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    required
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.password}
                    inputProps={{ maxLength: 70 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    error={Boolean(touched.confirm && errors.confirm)}
                    fullWidth
                    helperText={touched.confirm && errors.confirm}
                    label="Confirmation"
                    margin="normal"
                    name="confirm"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    required
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.confirm}
                    inputProps={{ maxLength: 70 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Box sx={{ py: 2 }}>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      endIcon={<ChevronRightIcon/>}
                    >
                      Modifier
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          }
        </Container>
      </Box>
    </>
  );
};

export default ResetPassword;
